import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import './ProductWeightsPage.css';
import PageHeader from '../../../shared/PageHeader';
import BrowseNav, { useBrowseIds } from '../../../shared/BrowseNav';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { AvailabilityPill } from '../../../shared/Pill';
import { Info } from 'lucide-react';
import NumberInput from '../../../shared/NumberInput';
import { formatDate } from '../../../shared/tenantClock';

// "Actual · 3 Mar 2026" (measured on Warehouse › Weigh & Measure) or
// "Estimate · 3 Mar 2026" (typed here); "Estimate" alone for a figure from
// before these were kept (services/measureSources.js).
function sourceCaption(sources, key) {
  const entry = sources?.[key];
  const label = entry?.source === 'actual' ? 'Actual' : 'Estimate';
  return entry?.on ? `${label} · ${formatDate(entry.on)}` : label;
}

// Weight/dimension entry on the shared NumberInput — typed and shown in
// the company Number Format, 1 decimal place once committed and up to 3
// while editing (this page's step="0.001"). Commits on blur. See "Numbers
// and Currency" in docs/product/platform/architecture/ui-architecture.md.
function WeightDimField({ value, onCommit, disabled }) {
  return (
    <NumberInput
      value={value === null || value === undefined ? '' : value}
      minDecimals={1}
      maxDecimals={1}
      min="0"
      placeholder="—"
      disabled={disabled}
      onBlur={(e) => {
        const raw = e.target.value;
        const val = raw === '' ? null : Number(raw);
        const current = value === null || value === undefined ? null : Number(value);
        if (val !== current) onCommit(val);
      }}
      style={{ width: 120, textAlign: 'right' }}
    />
  );
}

// Weights & Dimensions (issue #100): a UoM row's own packed weight/
// footprint as it's actually bought or sold — a Reel's spool-and-all
// weight, a Box's own footprint, a coiled cable's own weight even sold
// loose in its Base UoM. Each of the four fields is entered directly, with
// no formula or derivation between rows (packaging varies too much per
// UoM for a computed total to hold up) — and never describes the item's
// own fixed specs (a cable's diameter etc. lives on Product Attributes,
// a separate concept). Sibling to ProductUomPage.js/ProductPricesPage.js/
// ProductMediaPage.js — same layout and header cross-link convention.
function ProductWeightsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const browseIds = useBrowseIds();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [weightUnit, setWeightUnit] = useState('');
  const [dimensionUnit, setDimensionUnit] = useState('');

  const fetchData = async () => {
    try {
      const res = await axios.get(`/api/products/${id}/uoms`);
      setData(res.data.data);
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Could not load Weights & Dimensions');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    axios.get('/api/platform-settings')
      .then((res) => {
        setWeightUnit(res.data.data?.weight_unit || '');
        setDimensionUnit(res.data.data?.dimension_unit || '');
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (loadError) return <div style={{ padding: 24, color: 'red' }}>{loadError}</div>;
  if (!data) return null;

  const { product } = data;
  const uoms = [...data.uoms].sort((a, b) => a.conversion_rate - b.conversion_rate);

  const handleUpdateRow = async (rowId, patch) => {
    try {
      await axios.put(`/api/products/${id}/uoms/${rowId}`, patch);
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not update Weights & Dimensions');
      await fetchData();
    }
  };

  const weightLabel = weightUnit ? `Weight (${weightUnit})` : 'Weight';
  const dimLabel = (axis) => (dimensionUnit ? `${axis} (${dimensionUnit})` : axis);

  const unitsNotSet = !weightUnit || !dimensionUnit;

  return (
    <div className="product-weights-page" style={{ width: '100%', maxWidth: 1000, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 24px 32px' }}>
      <PageHeader
        crumbs={[
          { label: 'Products', onClick: () => navigate('/products') },
          { label: product.sku },
          { label: 'Weights & Dimensions' },
        ]}
        title="Weights & Dimensions"
        subtitle={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <strong>{product.sku} — {product.name}</strong>
            <AvailabilityPill badge={product.availability_badge} date={product.availability_date} color={product.availability_badge_color} showDate />
          </span>
        )}
        meta={(
          <>
            <button type="button" className="link-button" onClick={() => navigate(`/products/${id}/uom`, { state: browseIds ? { browseIds } : undefined })}>Product Units of Measure →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/sales-prices?product_id=${id}`)}>Prices →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/products/${id}/media`, { state: browseIds ? { browseIds } : undefined })}>Product Media →</button>
          </>
        )}
      />

      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Weights & Dimensions
          <span style={{ marginLeft: 8, fontWeight: 400, textTransform: 'none' }}>{product.sku}</span>
        </h4>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          These describe each Unit of Measure as packed for shipping/receiving
          — a Box or Reel's own weight and footprint, or a loose item coiled/
          wrapped for shipping. Not the item's own fixed specs (diameter,
          shape) — those live elsewhere. Figures typed here are Estimates;
          Actuals are recorded on Warehouse › Weigh &amp; Measure.
        </div>

        {unitsNotSet && (
          <div style={{ fontSize: 12, color: '#8a5a00', background: '#fff8e1', border: '1px solid #f0d998', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            Product UoM Weight Unit and/or Product UoM Dimension Unit hasn't been set yet — set it under{' '}
            <button type="button" className="link-button" style={{ fontSize: 12 }} onClick={() => navigate('/global-platform-settings')}>
              Platform Settings → Weight &amp; Dimension Units
            </button>{' '}
            before entering values here.
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead className="list-head">
            <tr>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Unit of Measure</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{weightLabel}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{dimLabel('Length')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{dimLabel('Width')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{dimLabel('Height')}</th>
            </tr>
          </thead>
          <tbody>
            {uoms.map((row) => {
              const disabled = !row.can_buy && !row.can_sell;
              const title = unitsNotSet
                ? "Set Product UoM Weight Unit and Product UoM Dimension Unit under Platform Settings first"
                : disabled
                  ? 'Only applies to a Unit of Measure that can be bought or sold'
                  : undefined;
              return (
                <tr key={row.id} className="list-row">
                  <td style={{ padding: '6px 12px', fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {row.code} — {row.name}
                      {/* Always-visible marker, not just a hover-only title
                          on the disabled inputs — a disabled field gives no
                          visual cue there's an explanation to find, so this
                          icon is the persistent "there's a reason" signal;
                          its own title is the actual hover explanation. */}
                      {disabled && (
                        <span title={title} style={{ display: 'inline-flex', cursor: 'help' }}>
                          <Info size={13} strokeWidth={2.5} style={{ color: '#c98a1f' }} />
                        </span>
                      )}
                    </span>
                    {row.is_base && <span style={{ display: 'block', fontSize: 11, color: '#1976d2', fontWeight: 700 }}>BASE UOM</span>}
                  </td>
                  {['weight', 'length', 'width', 'height'].map((field) => (
                    // title sits on the <td>, not the <input> — a disabled
                    // form control doesn't fire the hover events a native
                    // title tooltip needs in most browsers, so put it on a
                    // wrapping element that isn't itself disabled.
                    <td key={field} style={{ padding: '6px 12px', textAlign: 'right' }} title={title}>
                      <WeightDimField
                        value={row[field]}
                        disabled={disabled || unitsNotSet}
                        onCommit={(val) => handleUpdateRow(row.id, { [field]: val })}
                      />
                      {row[field] !== null && row[field] !== undefined && (
                        <div style={{ fontSize: 11, color: '#8a94a6', marginTop: 2 }}>{sourceCaption(row.measure_sources, field)}</div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <BrowseNav id={id} ids={browseIds} buildPath={(targetId) => `/products/${targetId}/weights`} noun="Products" />
    </div>
  );
}

export default ProductWeightsPage;
